import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  Fragment,
  type FC,
  type Ref,
  type ChangeEvent,
  type PointerEvent,
} from 'react';
import * as Popover from '@radix-ui/react-popover';
import {
  getCountries,
  getCountryCallingCode,
  type Country,
} from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import en from 'react-phone-number-input/locale/en.json';
import { ChevronDown, Search, Check } from 'lucide-react';
import { FieldLabel, ICON_SIZE, useShake } from '@mokoboko/components';

import { XCloseIcon } from '@/icons';
import { mergeClassNames, toNationalPhone } from '@/lib/utils';

const FOCUS_DELAY_MS = 50;

interface PhoneChangeMeta {
  hadPlus?: boolean;
  hadIDD?: boolean;
}

interface CountryOption {
  value: Country;
  label: string;
}

interface SmartPhoneInputProps {
  countries?: Country[];
  country: Country;
  onCountryChange: (country: Country) => void;
  phone: string;
  onPhoneChange: (value: string, meta?: PhoneChangeMeta) => void;
  onRawInputChange?: (rawValue: string) => boolean;
  phoneHintText?: string;
  error?: boolean;
  errorText?: string;
  showLabel?: boolean;
  isRequired?: boolean;
  /** Set to false when used inside a modal/dialog to prevent portal conflicts */
  usePortal?: boolean;
  shake?: number;
  inputRef?: Ref<HTMLInputElement>;
  /** Fires only when focus truly leaves the widget — not when the country dropdown opens. */
  onBlur?: () => void;
}

interface FlagIconProps {
  country: Country;
}

const extractDigits = (value: string): string => value.replace(/\D+/g, '');

const buildCountryOptions = (countries: Country[]): CountryOption[] =>
  countries
    .map((c) => ({
      value: c,
      label: `${en[c]} (+${getCountryCallingCode(c)})`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

const COUNTRY_SEARCH_ALIASES: Partial<Record<Country, string[]>> = {
  US: ['usa', 'america'],
};

const filterCountryOptions = (
  options: CountryOption[],
  searchTerm: string
): CountryOption[] => {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return options;
  return options.filter(
    (opt) =>
      opt.label.toLowerCase().includes(term) ||
      COUNTRY_SEARCH_ALIASES[opt.value]?.some((alias) => alias.includes(term))
  );
};

const BORDER_DEFAULT = 'var(--brand-gray-300, #D5D7DA)';
const BORDER_OPEN = 'var(--brand-button, #F97066)';
const BORDER_ERROR = '#F04438';
const SHADOW_OPEN =
  '0 1px 2px 0 rgba(41,37,36,0.05), 0 0 0 3px var(--brand-red-100, #FEE4E2)';

function getTriggerBorderColor(isOpen: boolean, hasError: boolean): string {
  if (hasError) return BORDER_ERROR;
  if (isOpen) return BORDER_OPEN;
  return BORDER_DEFAULT;
}

function getInputBorderColor(isFocused: boolean, hasError: boolean): string {
  if (hasError) return BORDER_ERROR;
  if (isFocused) return BORDER_OPEN;
  return BORDER_DEFAULT;
}

const FlagIcon: FC<FlagIconProps> = ({ country }) => {
  const CountryFlag = flags[country] as
    | FC<React.SVGProps<SVGSVGElement>>
    | undefined;
  if (!CountryFlag) return null;
  return (
    <span className="inline-flex h-5 w-5 shrink-0 items-center [&_svg]:h-full [&_svg]:w-full [&_svg]:rounded-sm">
      <CountryFlag />
    </span>
  );
};

const SmartPhoneInput: FC<SmartPhoneInputProps> = ({
  countries: countriesProp,
  country,
  onCountryChange,
  phone,
  onPhoneChange,
  onRawInputChange,
  phoneHintText,
  error = false,
  errorText,
  showLabel = true,
  isRequired = false,
  usePortal = true,
  shake: shakeTrigger = 0,
  inputRef,
  onBlur,
}) => {
  const { shakeRef, onShakeEnd } = useShake<HTMLDivElement>(shakeTrigger);
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isTriggerHovered, setIsTriggerHovered] = useState(false);
  const [isInputHovered, setIsInputHovered] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const resolvedCountries = useMemo(
    () =>
      countriesProp && countriesProp.length > 0
        ? countriesProp
        : (getCountries() as Country[]),
    [countriesProp]
  );
  const countryOptions = useMemo(
    () => buildCountryOptions(resolvedCountries),
    [resolvedCountries]
  );
  const filteredOptions = useMemo(
    () => filterCountryOptions(countryOptions, searchTerm),
    [countryOptions, searchTerm]
  );

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      return;
    }

    const timeoutId = setTimeout(() => {
      searchInputRef.current?.focus();
    }, FOCUS_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [isOpen]);

  const handleCountrySelect = useCallback(
    (selectedCountry: Country) => {
      onCountryChange(selectedCountry);
      setIsOpen(false);
    },
    [onCountryChange]
  );

  const handlePhoneChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const handled = onRawInputChange?.(inputValue);
      if (handled) return;

      const rawDigits = extractDigits(inputValue);
      const trimmed = inputValue.trim();

      onPhoneChange(rawDigits, {
        hadPlus: trimmed.includes('+'),
        hadIDD: trimmed.startsWith('00'),
      });
    },
    [onPhoneChange, onRawInputChange]
  );

  const handleClearPointerDown = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
    },
    []
  );

  const handleClearClick = useCallback(() => {
    setSearchTerm('');
    searchInputRef.current?.focus();
  }, []);

  const PortalWrapper = usePortal ? Popover.Portal : Fragment;

  const triggerBorderColor = getTriggerBorderColor(isOpen, error);
  const inputBorderColor = getInputBorderColor(isFocused, error);

  return (
    <div
      onBlur={(e) => {
        // The dropdown is portaled outside this root, so while it's open focus
        // moving into it must not count as the user leaving the field.
        if (isOpen) return;
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        const normalized = toNationalPhone(phone ?? '', country);
        if (normalized !== (phone ?? '').replace(/\D/g, '')) {
          onPhoneChange(normalized);
        }
        onBlur?.();
      }}
    >
      {showLabel && (
        <FieldLabel isRequired={isRequired}>Phone number</FieldLabel>
      )}

      <div className="relative">
        <div
          ref={shakeRef}
          onAnimationEnd={onShakeEnd}
          className="mb-shake flex"
        >
          <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
            <Popover.Trigger
              className="mb-dd-trigger flex shrink-0 cursor-pointer items-center gap-1.5 rounded-l-xl border border-r-0 bg-white px-3 py-2.5 text-base font-medium shadow-xs outline-none transition-colors"
              data-hovered={isTriggerHovered || undefined}
              data-error={error || undefined}
              onMouseEnter={() => setIsTriggerHovered(true)}
              onMouseLeave={() => setIsTriggerHovered(false)}
              style={{
                borderColor: triggerBorderColor,
                boxShadow: isOpen ? SHADOW_OPEN : undefined,
              }}
            >
              <FlagIcon country={country} />
              <span className="text-gray-900">
                +{getCountryCallingCode(country)}
              </span>
              <ChevronDown
                size={ICON_SIZE.sm}
                className={mergeClassNames(
                  'shrink-0 text-red-400 transition-transform duration-300',
                  isOpen && 'rotate-180'
                )}
                aria-hidden="true"
              />
            </Popover.Trigger>

            <PortalWrapper>
              <Popover.Content
                align="start"
                sideOffset={4}
                className="data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 z-popover w-[280px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg outline-none"
              >
                <div className="flex-shrink-0 border-b border-gray-200 px-1 py-1">
                  <div className="relative flex items-center gap-3 rounded-lg px-3 py-2 pr-9">
                    <Search
                      size={ICON_SIZE.md}
                      className="shrink-0 text-gray-500"
                      aria-hidden="true"
                    />
                    <input
                      ref={searchInputRef}
                      type="text"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="none"
                      spellCheck={false}
                      placeholder="Search countries"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="min-w-0 flex-1 bg-transparent text-base leading-6 text-gray-900 outline-none placeholder:text-gray-500"
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        aria-label="Clear search"
                        onPointerDown={handleClearPointerDown}
                        onClick={handleClearClick}
                        className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center justify-center text-gray-400 hover:text-gray-600"
                      >
                        <XCloseIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div
                  ref={listRef}
                  className="flex max-h-72 flex-col gap-0.5 overflow-y-auto p-1"
                >
                  {filteredOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleCountrySelect(option.value)}
                      className={mergeClassNames(
                        'flex w-full cursor-pointer select-none items-center gap-3 rounded-lg px-3 py-2 text-left text-base font-medium text-gray-900 transition-colors hover:bg-red-50 focus:outline-none',
                        option.value === country && 'bg-red-50'
                      )}
                    >
                      <FlagIcon country={option.value} />
                      <span className="font-semibold text-gray-900">
                        {option.value}
                      </span>
                      <span className="font-normal text-gray-500">
                        +{getCountryCallingCode(option.value)}
                      </span>
                      <span className="flex-1" />
                      <Check
                        size={ICON_SIZE.xs}
                        className={mergeClassNames(
                          'shrink-0 text-red-400',
                          option.value === country ? 'opacity-100' : 'opacity-0'
                        )}
                        aria-hidden="true"
                      />
                    </button>
                  ))}
                  {filteredOptions.length === 0 && (
                    <div className="flex select-none flex-col items-center gap-2 px-3 py-5">
                      <Search
                        size={ICON_SIZE.md}
                        className="text-gray-400"
                        aria-hidden="true"
                      />
                      <p className="text-center text-sm leading-5 text-gray-600">
                        No results for &ldquo;{searchTerm}&rdquo;
                      </p>
                    </div>
                  )}
                </div>
              </Popover.Content>
            </PortalWrapper>
          </Popover.Root>

          <input
            ref={inputRef}
            type="tel"
            autoComplete="tel"
            placeholder=""
            value={phone?.replace(/\D/g, '') ?? ''}
            onChange={handlePhoneChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            data-hovered={isInputHovered || undefined}
            data-error={error || undefined}
            onMouseEnter={() => setIsInputHovered(true)}
            onMouseLeave={() => setIsInputHovered(false)}
            className={mergeClassNames(
              'mb-input min-w-0 flex-1 rounded-r-xl border bg-white px-3 py-2.5 text-base shadow-xs outline-none transition-colors',
              'placeholder:text-base placeholder:text-gray-500',
              'text-gray-900'
            )}
            style={{
              borderColor: inputBorderColor,
              boxShadow: isFocused && !error ? SHADOW_OPEN : undefined,
            }}
          />
        </div>
      </div>

      {error && errorText && (
        <p role="alert" className="mt-1.5 text-sm font-normal text-error-600">
          {errorText}
        </p>
      )}
      {!error && phoneHintText && (
        <p className="mt-1.5 text-sm font-normal text-gray-600">
          {phoneHintText}
        </p>
      )}
    </div>
  );
};

export default SmartPhoneInput;
